// src/components/admin/services/EditServices.jsx
import { useState, useEffect, useCallback } from 'react';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash,
  FaSave,
  FaTimes,
  FaTools,
  FaSpinner,
  FaLeaf,
  FaSeedling,
  FaSnowflake,
  FaTree,
  FaWater,
  FaHammer,
  FaCut,
  FaSpa,
  FaHome,
  FaGlobe,
  FaLightbulb,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import { GiTreeBranch } from "react-icons/gi";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const EditServices = ({ 
  serviceList,
  serviceListMeta,
  updatingServices, 
  onAddService, 
  onUpdateService, 
  onDeleteService,
  onMoveUp,
  onMoveDown,
  onSuccess 
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'FaTools'
  });
  
  // State for storing user names
  const [userNames, setUserNames] = useState({});
  const [loadingUserName, setLoadingUserName] = useState(false);

  // Available icons for services
  const availableIcons = [
    { value: 'FaTools', label: 'Tools', component: FaTools },
    { value: 'FaLeaf', label: 'Leaf', component: FaLeaf },
    { value: 'FaSeedling', label: 'Seedling', component: FaSeedling },
    { value: 'FaSnowflake', label: 'Snowflake', component: FaSnowflake },
    { value: 'FaTree', label: 'Tree', component: FaTree },
    { value: 'FaWater', label: 'Water', component: FaWater },
    { value: 'FaHammer', label: 'Hammer', component: FaHammer },
    { value: 'FaCut', label: 'Cut', component: FaCut },
    { value: 'FaSpa', label: 'Spa', component: FaSpa },
    { value: 'FaHome', label: 'Home', component: FaHome },
    { value: 'FaGlobe', label: 'Globe', component: FaGlobe },
    { value: 'FaLightbulb', label: 'Lightbulb', component: FaLightbulb },
    { value: 'GiTreeBranch', label: 'Tree Branch', component: GiTreeBranch }
  ];

  // Fetch user name by UID - memoized to prevent infinite re-renders
  const fetchUserName = useCallback(async (uid) => {
    if (!uid || userNames[uid]) return;
    
    setLoadingUserName(true);
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const fullName = `${userData.firstName || ''} ${userData.lastName || ''}`.trim();
        setUserNames(prev => ({
          ...prev,
          [uid]: fullName || userData.email || 'Unknown User'
        }));
      } else {
        setUserNames(prev => ({
          ...prev,
          [uid]: 'Unknown User'
        }));
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      setUserNames(prev => ({
        ...prev,
        [uid]: 'Unknown User'
      }));
    } finally {
      setLoadingUserName(false);
    }
  }, [userNames]);

  // Fetch user names when serviceListMeta changes
  useEffect(() => {
    if (serviceListMeta?.lastUpdatedBy) {
      fetchUserName(serviceListMeta.lastUpdatedBy);
    }
    if (serviceListMeta?.createdBy) {
      fetchUserName(serviceListMeta.createdBy);
    }
  }, [serviceListMeta?.lastUpdatedBy, serviceListMeta?.createdBy, fetchUserName]);

  // Get icon component by value
  const getIconComponent = (iconValue) => {
    const icon = availableIcons.find(i => i.value === iconValue);
    return icon ? icon.component : FaTools;
  };

  // Format last updated info
  const formatLastUpdated = () => {
    if (!serviceListMeta?.updatedAt) return null;
    
    try {
      const date = serviceListMeta.updatedAt.toDate ? 
        serviceListMeta.updatedAt.toDate() : 
        new Date(serviceListMeta.updatedAt);
      
      const formattedDate = date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long', 
        day: 'numeric'
      });
      
      const formattedTime = date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      
      const updatedByUid = serviceListMeta.lastUpdatedBy;
      const updatedByName = updatedByUid ? 
        (userNames[updatedByUid] || (loadingUserName ? 'Loading...' : updatedByUid)) :
        'Unknown';
      
      return `Last updated ${formattedDate} at ${formattedTime} by ${updatedByName}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Recently updated';
    }
  };

  // Handle form changes
  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Start adding new service
  const startAdding = () => {
    setFormData({ name: '', description: '', icon: 'FaTools' });
    setShowAddForm(true);
  };

  // Start editing service
  const startEditing = (service) => {
    setFormData({
      name: service.name,
      description: service.description || '',
      icon: service.icon || 'FaTools'
    });
    setEditingService(service.name);
  };

  // Cancel form
  const cancelForm = () => {
    setShowAddForm(false);
    setEditingService(null);
    setFormData({ name: '', description: '', icon: 'FaTools' });
  };

  // Handle add service
  const handleAddService = async () => {
    if (!formData.name.trim()) return;

    try {
      await onAddService(formData);
      cancelForm();
      onSuccess?.('Service added successfully!');
    } catch (err) {
      console.error('Failed to add service:', err);
    }
  };

  // Handle update service
  const handleUpdateService = async (oldName) => {
    if (!formData.name.trim()) return;

    try {
      await onUpdateService(oldName, formData);
      cancelForm();
      onSuccess?.('Service updated successfully!');
    } catch (err) {
      console.error('Failed to update service:', err);
    }
  };

  // Handle delete service
  const handleDeleteService = async (serviceName) => {
    if (!window.confirm(`Are you sure you want to delete "${serviceName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await onDeleteService(serviceName);
      onSuccess?.('Service deleted successfully!');
    } catch (err) {
      console.error('Failed to delete service:', err);
    }
  };

  // Handle move up
  const handleMoveUp = async (serviceName) => {
    try {
      await onMoveUp(serviceName);
    } catch (err) {
      console.error('Failed to move service up:', err);
    }
  };

  // Handle move down
  const handleMoveDown = async (serviceName) => {
    try {
      await onMoveDown(serviceName);
    } catch (err) {
      console.error('Failed to move service down:', err);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Service List Management</h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage and reorder your company's list of available services
            </p>
          </div>
          <button
            onClick={startAdding}
            disabled={updatingServices}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FaPlus className="w-4 h-4 mr-2" />
            Add Service
          </button>
        </div>
        
        {/* Metadata info */}
        <div className="mt-4 text-xs text-gray-500 space-y-1">
          {formatLastUpdated() && (
            <div>{formatLastUpdated()}</div>
          )}
        </div>
      </div>

      {/* Services List */}
      <div className="p-6">
        {serviceList.length === 0 ? (
          <div className="text-center py-8">
            <FaTools className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Services Yet</h4>
            <p className="text-gray-500 mb-4">
              Add your first service to get started with managing your service offerings.
            </p>
            <button
              onClick={startAdding}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            >
              <FaPlus className="w-4 h-4 mr-2" />
              Add First Service
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {serviceList.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              const isEditing = editingService === service.name;
              const isFirst = index === 0;
              const isLast = index === serviceList.length - 1;

              return (
                <div key={service.name} className="border border-gray-200 rounded-lg p-4">
                  {isEditing ? (
                    // Edit Form
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Service Name *
                          </label>
                          <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleFormChange('name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Enter service name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Icon
                          </label>
                          <select
                            value={formData.icon}
                            onChange={(e) => handleFormChange('icon', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            {availableIcons.map((icon) => (
                              <option key={icon.value} value={icon.value}>
                                {icon.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => handleFormChange('description', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Enter service description (optional)"
                        />
                      </div>
                      
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={cancelForm}
                          disabled={updatingServices}
                          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 disabled:opacity-50 transition-colors"
                        >
                          <FaTimes className="w-4 h-4 mr-2 inline" />
                          Cancel
                        </button>
                        <button
                          onClick={() => handleUpdateService(service.name)}
                          disabled={updatingServices || !formData.name.trim()}
                          className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {updatingServices ? (
                            <FaSpinner className="w-4 h-4 mr-2 inline animate-spin" />
                          ) : (
                            <FaSave className="w-4 h-4 mr-2 inline" />
                          )}
                          Save Changes
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Service Display with Reorder Controls
                    <div className="flex items-center justify-between gap-4">
                      {/* Reorder Controls - Left side */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleMoveUp(service.name)}
                          disabled={updatingServices || isFirst}
                          className={`p-1 rounded transition-colors ${
                            isFirst 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Move up"
                        >
                          <FaArrowUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMoveDown(service.name)}
                          disabled={updatingServices || isLast}
                          className={`p-1 rounded transition-colors ${
                            isLast 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Move down"
                        >
                          <FaArrowDown className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Service Info - Center */}
                      <div className="flex items-center space-x-4 flex-1">
                        <div className="text-primary-600">
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="text-base font-medium text-gray-900">{service.name}</h4>
                            <span className="text-xs text-gray-400">#{service.displayOrder || index + 1}</span>
                          </div>
                          {service.description && (
                            <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Action Buttons - Right side */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => startEditing(service)}
                          disabled={updatingServices}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-50 transition-colors"
                          title="Edit service"
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteService(service.name)}
                          disabled={updatingServices}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50 transition-colors"
                          title="Delete service"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="mt-6 border border-primary-200 rounded-lg p-4 bg-primary-50">
            <h4 className="text-base font-medium text-gray-900 mb-4">Add New Service</h4>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Enter service name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Icon
                  </label>
                  <select
                    value={formData.icon}
                    onChange={(e) => handleFormChange('icon', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {availableIcons.map((icon) => (
                      <option key={icon.value} value={icon.value}>
                        {icon.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter service description (optional)"
                />
              </div>
              
              <div className="flex justify-end space-x-2">
                <button
                  onClick={cancelForm}
                  disabled={updatingServices}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  <FaTimes className="w-4 h-4 mr-2 inline" />
                  Cancel
                </button>
                <button
                  onClick={handleAddService}
                  disabled={updatingServices || !formData.name.trim()}
                  className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {updatingServices ? (
                    <FaSpinner className="w-4 h-4 mr-2 inline animate-spin" />
                  ) : (
                    <FaPlus className="w-4 h-4 mr-2 inline" />
                  )}
                  Add Service
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditServices;